import {useCallback, useEffect, useReducer, useState} from 'react';

import {BusinessTransactionChannel} from '@afriex/core/domain/enums';
import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {settlementAccountService} from '@/services/settlementAccountService';
import type {SettlementPaymentMethodPayload} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {
  SettlementAccountValidationError,
  SettlementAccountValidator,
} from '@/validators/SettlementAccountValidator';

import type {SettlementAccountFieldError} from './types';
import {SettlementAccountActionType} from './types';
import updateSettlementAccountReducer from './updateSettlementAccountReducer';

// A new account starts on USD, which most are opened in. The picker offers
// every Afriex currency, so this is a starting point rather than a restriction.
const emptyAccount = (): Partial<SettlementPaymentMethodPayload> => ({
  channel: BusinessTransactionChannel.BANK_ACCOUNT,
  currencyCode: AfriexCurrencyCode.USD,
  meta: {flags: {isDeactivated: false}},
});

const useSettlementAccountUpdate = (
  onClose: VoidFunction,
  initialData?: SettlementPaymentMethod,
  onSuccess?: () => void | Promise<void>,
) => {
  const [account, dispatch] = useReducer(
    updateSettlementAccountReducer,
    initialData ?? emptyAccount(),
  );
  const [isLoading, setIsLoading] = useState(false);
  // Held here rather than in the form so it survives a re-render and is
  // cleared by the same edit that invalidates it.
  const [fieldError, setFieldError] =
    useState<SettlementAccountFieldError | null>(null);

  useEffect(() => {
    dispatch({
      type: SettlementAccountActionType.RESET_SETTLEMENT_ACCOUNT,
      payload: initialData ?? emptyAccount(),
    });
  }, [initialData]);

  const clearFieldError = useCallback(() => setFieldError(null), []);

  const handleSubmit = async (raw: Partial<SettlementPaymentMethodPayload>) => {
    setIsLoading(true);
    setFieldError(null);
    try {
      const valid = new SettlementAccountValidator(raw).value();
      if (initialData?.id) {
        await settlementAccountService.updateSettlementPaymentMethodById(
          initialData.id,
          valid,
        );
        showSuccessMessage('Settlement account updated');
      } else {
        await settlementAccountService.createSettlementPaymentMethod(valid);
        showSuccessMessage('Settlement account saved');
      }
      onClose();
      // Refetch in place rather than reloading the window, so the operator stays
      // on the page and the filters they set survive.
      await onSuccess?.();
    } catch (e) {
      // A rejection that names a field is shown on that field, where the
      // operator is looking. Anything else — a transport failure, a refusal
      // that belongs to no single input — still needs the toast.
      if (e instanceof SettlementAccountValidationError && e.field) {
        setFieldError({field: e.field, message: e.message});
      } else {
        showErrorMessage(
          `Error saving settlement account: ${getErrorMessage(e, String(e))}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    account,
    dispatch,
    handleSubmit,
    isLoading,
    fieldError,
    clearFieldError,
  };
};

export default useSettlementAccountUpdate;
